import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { api } from "shared/api";

interface apiSetOrderVoucherProps {
  orderID: string;
  voucher?: string;
  orderType: string;
}

// Define the function to set the order voucher based on the order type
const apiSetOrderVoucher = async ({
  orderID,
  voucher,
  orderType,
}: apiSetOrderVoucherProps) => {
  // If the order type is 'd', call the gersSetOrderVoucher API endpoint
  if (orderType === "d") {
    return api()
      .gersSetOrderVoucher(orderID, voucher)
      .then((res) => res.data);
  } else {
    // If the order type is 'p', call the gersSetStandardOrderVoucher API endpoint
    return api()
      .gersSetStandardOrderVoucher(orderID, voucher)
      .then((res) => res.data);
  }
};

export const useVoucherOrderAddition = ({
  orderID,
  orderType,
  toastEnabled = true,
}: {
  orderID: string | null;
  orderType: string;
  toastEnabled?: boolean;
}) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: setOrderVoucher,
    status,
    reset: resetAddVoucherToOrder,
  } = useMutation(apiSetOrderVoucher, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-standard-order-${orderID}`],
      });
      if (toastEnabled) {
        toast.success("Voucher is toegevoegd!");
      }
    },
  });

  // Function to add a voucher to the order
  const addVoucherToOrder = async (voucher: string) => {
    if (!orderID) return; // If orderID is null, return early
    try {
      await setOrderVoucher({ orderID, voucher, orderType }); // Call the setOrderVoucher function with the provided parameters
      return true; // Return true if the voucher is added successfully
    } catch (error: any) {
      toast.error("Voucher is ongeldig!"); // Display an error toast if there is an error adding the voucher
      return false; // Return false if there is an error adding the voucher
    }
  };

  return { addVoucherToOrder, status, resetAddVoucherToOrder };
};
